/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, ReactNode} from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio, {RadioProps} from '@material-ui/core/Radio';
import {makeStyles} from '@material-ui/core/styles';
import {ClassNameMap} from '@material-ui/styles';
import MaterialRadioButtonIcon from '@ion/dls/src/icons/current/MaterialRadioButtonIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

interface RadioTypes extends RadioProps {
  label: string | ReactNode;
  labelClasses?: Partial<ClassNameMap<string>>
};

const useStyles = makeStyles(() => ({
  root : {
    margin    : -3,
    padding   : 10,
    '&:hover' : {
      backgroundColor : 'transparent',
      '& $ripple'     : {
        opacity : 0.5
      },
      '& $ring' : {
        stroke : dlsColor('aaBlue')
      },
      '& $checkedIcon $ring' : {
        r           : '8.5px',
        strokeWidth : 3
      },
      '& $checkedIcon $dot' : {
        r : '4px'
      }
    },
    '&:active' : {
      '& $ripple' : {
        opacity : 0.5
      },
      '& $ring' : {
        r           : '8px',
        stroke      : dlsColor('aaBlue'),
        strokeWidth : 4
      },
      '& $checkedIcon $ring' : {
        r           : '8px',
        strokeWidth : 4
      },
      '& $checkedIcon $dot' : {
        r : '4px'
      }
    },
    '&.Mui-focusVisible' : {
      '& $halo' : {
        opacity : 1
      },
      '& $ring' : {
        r : '8px'
      }
    },
    '&$disabled' : {
      '& $ring' : {
        stroke : dlsColor('grey4')
      },
      '& $dot' : {
        fill : dlsColor('grey4')
      }
    }
  },
  checkedIcon : {},
  ring        : {
    transition : 'r 100ms, stroke 200ms, stroke-width 100ms'
  },
  dot : {
    transition : 'r 100ms'
  },
  ripple : {
    transition : 'opacity 200ms'
  },
  halo : {
    transition : 'opacity 200ms'
  },
  disabled     : {},
  controlLabel : {
    '&.Mui-disabled .MuiFormControlLabel-label' : {
      color : dlsColor('grey4')
    }
  }
}));

/**
 * A customized Material UI Radio button wrapped in a FormControlLabel.
 * It features custom SVG-like icons for checked, unchecked, and focused states
 * to match the DLS (Design Language System) specifications.
 *
 * ```tsx
 * <RadioButton label="Select Option"
 * checked={isSelected}
 * onChange={handleChange} />
 * 
 * ```
 * 
 */
export const RadioButton = forwardRef<HTMLButtonElement, RadioTypes>(({
  label,
  labelClasses,
  ...props
}: RadioTypes, ref) => {
  const classes = useStyles();

  const iconClasses = {
    ring   : classes.ring,
    dot    : classes.dot,
    ripple : classes.ripple,
    halo   : classes.halo
  };

  return (
    <FormControlLabel label={label}
      className={classes.controlLabel}
      classes={labelClasses}
      control={
        <Radio disableRipple disableFocusRipple
          classes={{root: classes.root, disabled: classes.disabled}}
          icon={<MaterialRadioButtonIcon classes={iconClasses} />}
          checkedIcon={<MaterialRadioButtonIcon checked
            className={classes.checkedIcon}
            classes={iconClasses} />}
          color='primary'
          ref={ref}
          {...props} />
      } />
  );
});

export default React.memo(RadioButton);
